// Account activation (phase 4): a Google sign-up starts "menunggu_aktivasi"
// until the admin activates it after payment. Admin and konsultan accounts
// are always active. A non-active account keeps plain chat only; projects,
// uploads, document generation, Library and E-Learning wait for activation
// (the API answers 403 account_inactive for them).
import type { Feature, User } from "../api";

/** True when the account may use the paid features. */
export function isActiveAccount(user: Pick<User, "role" | "status"> | null): boolean {
  if (!user) return false;
  // A missing status reads as aktif (older sessions); the API enforces it anyway.
  return user.role === "admin" || user.role === "konsultan" || (user.status ?? "aktif") === "aktif";
}

/** True for a Google sign-in account (the real one or the simulated one). */
export function isGoogleAccount(user: Pick<User, "authProvider"> | null): boolean {
  return !!user && (user.authProvider === "google" || user.authProvider === "google_demo");
}

/** The features a non-active account cannot use. */
export const PAID_FEATURES: readonly Feature[] = ["projects", "library", "elearning", "document_generation", "pantau_paket", "alur_kerja", "dashboard_penyedia", "templates"];

/** The friendly messages for /masuk?error=<code> after a failed Google sign-in. */
export const GOOGLE_ERROR_MESSAGE: Record<string, string> = {
  not_configured: "Login Google belum dikonfigurasi admin.",
  denied: "Masuk dengan Google dibatalkan.",
  state: "Sesi masuk kedaluwarsa. Coba lagi.",
  rate_limited: "Terlalu banyak percobaan masuk. Tunggu sebentar, lalu coba lagi.",
  exchange: "Google belum dapat memverifikasi proses masuk. Coba lagi.",
  id_token: "Data akun Google tidak dapat diverifikasi. Coba lagi.",
  email_unverified: "Email Google Anda belum terverifikasi.",
  account_conflict: "Email ini sudah terdaftar dengan cara masuk lain. Hubungi admin.",
  account_disabled: "Akun Anda dinonaktifkan. Hubungi admin.",
  wrong_account: "Akun Google yang dipilih berbeda dengan akun Anda. Pilih akun Google yang sama.",
  unavailable: "Layanan masuk Google sedang tidak tersedia. Coba lagi nanti.",
};

export function googleErrorMessage(code: string | null): string | null {
  if (!code) return null;
  return GOOGLE_ERROR_MESSAGE[code] ?? "Masuk dengan Google belum berhasil. Coba lagi.";
}

/** A safe same-origin path for ?next=, or null. */
export function safeNextPath(next: string | null | undefined): string | null {
  return next && next.startsWith("/") && !next.startsWith("//") && !next.startsWith("/\\") ? next : null;
}
